import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Link2, Pencil, Trash2 } from "lucide-react";
import type { PurchaseCost, PurchaseDocument, PurchaseOrderFile, PurchasePayment, PurchaseShipment } from "@/api/purchase";
import { useDeletePurchasePayment, usePatchPurchaseCost, usePatchPurchaseShipment } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fehlerText } from "@/lib/fehlerText";
import { formatEuroGenau } from "../board/InlineZellen";
import { parseBetrag } from "../board/zeilen";
import { formatUsd, shipmentStatusLabel, weStatusLabel } from "@/lib/purchase-format";
import { KostenSollAktionen, NeuerKostenposten, kostenPatchBody } from "../KostenSoll";
import {
  NeueZahlung, RechnungssummeFeld, ZAHLUNG_TYP_TEXT, ZahlungAnrechnung, ZahlungBearbeiten, type FehlerMelder,
} from "../ZahlungFormulare";
import type { FormularZiel } from "./AufgabeKarte";
import { BelegeListe } from "./Belege";

const datum = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" }) : "—";

const KOSTEN_TYP: Record<string, string> = { spedition: "Spedition", zoll: "Zoll", sonstig: "Sonstiges" };
const KOSTEN_STATUS: Record<string, string> = { erwartet: "erwartet", erfasst: "erfasst", entfaellt: "entfällt", geschaetzt: "geschätzt" };

const err: FehlerMelder = (rueckfall) => (e) => toast.error(fehlerText(e, rueckfall));

interface SeitenPanelsProps {
  order: PurchaseOrderFile;
  /** Ausgewaehlte Spur: ein Container oder null fuer "Bestellung gesamt". */
  shipment: PurchaseShipment | null;
  documents: PurchaseDocument[];
  /** Geldfelder sind nur mit Preisrecht gefuellt (Server maskiert mit null). */
  withPrices: boolean;
  saldo: "offen" | "bezahlt";
  /** Rechnung, Zahlungen und Kosten schreiben (article_purchase_prices). */
  darfPreise: boolean;
  /** Container-Daten aendern und Belege hochladen (article_purchase). */
  darfBearbeiten: boolean;
  /** Kosten ohne weitere Posten freigeben (admin_general). */
  darfKostenFreigeben: boolean;
  /** Ein Hauptknopf der Aufgabe-Karte will ein Formular hier; `n` zaehlt jeden Klick. */
  anfrage: { ziel: FormularZiel; n: number } | null;
  /** „C1 · TXGU1“ — derselbe Titel wie die Spur des Containers. */
  containerTitelVon: (shipment: PurchaseShipment) => string;
  /** Titel der Spur (z. B. „C1 · TXGU1“ oder „Bestellung“) fuer das Upload-Ziel. */
  zielText: string;
  uploadLaeuft: boolean;
  onBelegeHochladen: (dateien: File[]) => void;
  /** Dialoge der Akte: Review, „Dokument ablehnen“, „Packliste korrigieren“, „Dokument löschen“. */
  onBelegPruefen: (documentId: number) => void;
  onBelegAblehnen: (documentId: number) => void;
  onBelegKorrigieren: (documentId: number) => void;
  onBelegLoeschen: (documentId: number) => void;
}

/** Wie der Server bei `assign`: nach Zustellung oder Wareneingang nimmt ein Container keine Belege mehr an. */
const ZUSTELL_STATUS = new Set(["zugestellt", "im_wareneingang", "eingelagert"]);
const nimmtBelegeAn = (s: PurchaseShipment) => s.warehouse_receipt_id === null && !ZUSTELL_STATUS.has(s.status);

function Panel({ titel, pill, children }: { titel: string; pill?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section aria-label={titel} className="min-w-0 rounded-md border border-line bg-background px-3 py-2.5">
      <h4 className="mb-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 font-display text-[13px] font-bold text-ink">
        <span className="min-w-0">{titel}</span>{pill}
      </h4>
      {children}
    </section>
  );
}

function Pill({ ton, children }: { ton: "ok" | "warn" | "neutral"; children: React.ReactNode }) {
  const dot = ton === "ok" ? "bg-success" : ton === "warn" ? "bg-warn" : "bg-muted-2";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-background px-2 py-0.5 text-[11px] font-semibold text-ink-2">
      <span aria-hidden="true" className={`size-2 rounded-full ${dot}`} />{children}
    </span>
  );
}

/** Fokussiert ein Feld der Spalte und rollt es in Sicht (jsdom kennt scrollIntoView nicht). */
/** Rueckfall fuer ein aelteres Backend: wie ek-logic `effektiverKurs` nur Zahlungen mit USD- UND EUR-Betrag. */
function kursAusZahlungen(payments: ReadonlyArray<Pick<PurchasePayment, "amount_usd" | "amount_eur">>): number | null {
  let usd = 0;
  let eur = 0;
  for (const p of payments) {
    if (p.amount_usd !== null && p.amount_eur !== null && p.amount_usd > 0 && p.amount_eur > 0) {
      usd += p.amount_usd;
      eur += p.amount_eur;
    }
  }
  return usd > 0 && eur > 0 ? eur / usd : null;
}

function fokussiere(el: HTMLElement) {
  el.focus();
  el.scrollIntoView?.({ block: "nearest" });
}

/**
 * Rechte Spalte: Saldo, Kosten fuer den Misch-EK, Belege der Spur, Container-Daten.
 * Seit 21.10.2026 („alles in der Maske“) wird hier auch erfasst: Rechnungssumme, Zahlungen,
 * Kostenposten und Container-Daten — mit denselben Hooks und PATCH-Koerpern wie die Akte.
 */
export function SeitenPanels({
  order, shipment, documents, withPrices, saldo, darfPreise, darfBearbeiten, darfKostenFreigeben, anfrage,
  containerTitelVon, zielText, uploadLaeuft, onBelegeHochladen, onBelegPruefen, onBelegAblehnen, onBelegKorrigieren, onBelegLoeschen,
}: SeitenPanelsProps) {
  const wurzel = useRef<HTMLDivElement>(null);
  const [zahlungOffen, setZahlungOffen] = useState(false);
  const [neuerPostenFuer, setNeuerPostenFuer] = useState<number | null>(null);
  // Ziel, das nach dem naechsten Rendern den Fokus bekommt (das Formular muss erst erscheinen).
  const fokusZiel = useRef<string | null>(null);

  const darfGeld = darfPreise && withPrices;
  const gruppen = shipment ? [shipment] : order.shipments;

  // Spurwechsel: ein offenes Anlegeformular gehoert zum vorigen Container.
  const shipmentId = shipment?.id ?? null;
  useEffect(() => { setNeuerPostenFuer(null); }, [shipmentId]);

  useEffect(() => {
    if (!anfrage || !darfGeld) return;
    if (anfrage.ziel === "invoice") fokusZiel.current = "invoice";
    if (anfrage.ziel === "zahlung") { setZahlungOffen(true); fokusZiel.current = "zahlung"; }
    if (anfrage.ziel === "kosten") {
      // Erst der erste erwartete Posten (Betrag fehlt), sonst ein neuer Posten fuer den
      // Container ohne Posten — dort sperrt der Misch-EK.
      if (wurzel.current?.querySelector('[data-fokus-ziel="kosten-erwartet"]')) fokusZiel.current = "kosten-erwartet";
      else if (gruppen.length > 0) {
        setNeuerPostenFuer((gruppen.find((g) => g.costs.length === 0) ?? gruppen[0]!).id);
        fokusZiel.current = "kosten";
      }
    }
    // Nur auf einen neuen Klick reagieren, nicht auf jedes Neuladen der Akte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anfrage?.n]);

  useEffect(() => {
    if (fokusZiel.current === null) return;
    const el = wurzel.current?.querySelector<HTMLElement>(`[data-fokus-ziel="${fokusZiel.current}"]`);
    if (el) { fokusZiel.current = null; fokussiere(el); }
  });

  const invoice = order.invoice_total_usd;
  const paidUsd = order.paid_usd;
  const offenUsd = invoice !== null && paidUsd !== null ? invoice - paidUsd : null;
  // Bezahlt: dieselben Zahlungen wie paid_usd des Servers (alle Typen, auch Nebenkosten),
  // damit Bezahlt + Offen = Invoice. EUR nur, wo erfasst; fehlende werden genannt.
  const bezahltEur = order.payments.reduce((acc, p) => acc + (p.amount_eur ?? 0), 0);
  const mitEur = order.payments.filter((p) => p.amount_eur !== null).length;
  const ohneEur = order.payments.filter((p) => p.amount_eur === null && p.amount_usd !== null).length;
  const kurs = order.effektiver_kurs_eur_pro_usd !== undefined ? order.effektiver_kurs_eur_pro_usd : kursAusZahlungen(order.payments);

  const kosten = gruppen.flatMap((s) => s.costs);
  const erwartet = kosten.filter((c) => c.status === "erwartet").length;

  const titelVonId = (id: number) => {
    const sh = order.shipments.find((g) => g.id === id);
    return sh ? containerTitelVon(sh) : `Container #${id}`;
  };

  const belege = documents.filter((d) => shipment ? d.purchase_shipment_id === shipment.id : d.purchase_shipment_id === null);
  const belegAktionen = darfBearbeiten ? {
    orderId: order.id,
    ziele: [
      ...(shipment ? [{ shipmentId: null, titel: "Bestellung (ohne Container)" }] : []),
      ...order.shipments.filter((s) => s.id !== shipment?.id && nimmtBelegeAn(s)).map((s) => ({ shipmentId: s.id, titel: containerTitelVon(s) })),
    ],
    pruefen: onBelegPruefen,
    ablehnen: onBelegAblehnen,
    korrigieren: onBelegKorrigieren,
    loeschen: onBelegLoeschen,
  } : undefined;
  const neuerPostenShipment = gruppen.find((g) => g.id === neuerPostenFuer) ?? null;

  return (
    // minmax(0,1fr) statt der impliziten auto-Spur: sonst waechst die Spalte auf die
    // min-content-Breite des breitesten Kindes (langer Dateiname) und laeuft aus dem Raster.
    <div ref={wurzel} className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-3">
      <Panel titel="Saldo Bestellung" pill={<Pill ton={saldo === "bezahlt" ? "ok" : "warn"}>{saldo}</Pill>}>
        {withPrices ? (
          <>
            {darfGeld ? (
              <RechnungssummeFeld orderId={order.id} invoice={invoice} err={err} label="Invoice (USD)" knopf="Invoice speichern" kompakt />
            ) : (
              <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-[13px]">
                <Zeile label="Invoice">{formatUsd(invoice)}</Zeile>
              </dl>
            )}
            {order.payments.length > 0 ? (
              <ul aria-label="Zahlungen" className="mt-1.5 grid grid-cols-[minmax(0,1fr)] gap-1 text-[13px]">
                {order.payments.map((p) => (
                  <ZahlungsZeile key={p.id} zahlung={p} darf={darfGeld} shipments={order.shipments}
                    titelVon={titelVonId} vorwahl={shipment?.id ?? order.shipments[0]?.id ?? null} />
                ))}
              </ul>
            ) : null}
            <dl className="mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px]">
              {order.payments.length > 0 ? (
                <>
                  <dt className="mt-1 border-t border-line pt-1 text-ink-2">Bezahlt</dt>
                  <dd className="mt-1 grid justify-items-end border-t border-line pt-1 text-right font-mono [overflow-wrap:anywhere]">
                    <span>{formatUsd(paidUsd)}</span>
                    <span>{mitEur > 0 ? formatEuroGenau(bezahltEur) : "— €"}</span>
                    {ohneEur > 0 ? (
                      <span className="font-sans text-[11px] text-warn">{ohneEur === 1 ? "1 Zahlung" : `${ohneEur} Zahlungen`} ohne €-Betrag</span>
                    ) : null}
                  </dd>
                </>
              ) : null}
              <dt className="mt-1 border-t border-line pt-1 font-semibold text-ink">Offen</dt>
              <dd className="mt-1 border-t border-line pt-1 text-right font-mono font-semibold [overflow-wrap:anywhere]">{formatUsd(offenUsd)}</dd>
              <dt className="text-ink-2">Kurs aus Zahlungen</dt>
              <dd className="text-right font-mono [overflow-wrap:anywhere]">{kurs === null ? "—" : `${kurs.toLocaleString("de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4 })} €/$`}</dd>
            </dl>
            {darfGeld ? (
              zahlungOffen ? (
                <NeueZahlung orderId={order.id} err={err} kompakt
                  onAbbrechen={() => setZahlungOffen(false)} onGespeichert={() => setZahlungOffen(false)} />
              ) : (
                <Button type="button" variant="outline" size="sm" className="mt-2.5 w-full" onClick={() => setZahlungOffen(true)}>
                  Zahlung erfassen
                </Button>
              )
            ) : null}
          </>
        ) : (
          <p className="text-xs text-ink-2">Beträge sind nur mit Preisrecht sichtbar.</p>
        )}
      </Panel>

      <Panel
        titel="Kosten für Misch-EK"
        pill={erwartet > 0 ? <Pill ton="warn">{erwartet} erwartet</Pill> : kosten.length > 0 ? <Pill ton="ok">vollständig</Pill> : undefined}
      >
        {gruppen.length === 0 ? (
          <p className="text-xs text-ink-2">Noch kein Container: Kostenposten entstehen je Container.</p>
        ) : (
          <div className="grid grid-cols-[minmax(0,1fr)] gap-2.5">
            {gruppen.map((g) => (
              <div key={g.id}>
                {shipment ? null : <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-2">{containerTitelVon(g)}</div>}
                {g.costs.length === 0 ? (
                  <p className="text-xs text-ink-2">Noch keine Kostenposten. Ohne Spedition und Zoll bleibt der Misch-EK gesperrt.</p>
                ) : (
                  <ul className="grid grid-cols-[minmax(0,1fr)] gap-1 text-[13px]">
                    {g.costs.map((c) => <KostenZeile key={c.id} posten={c} darf={darfGeld} withPrices={withPrices} />)}
                  </ul>
                )}
                <KostenSollAktionen shipmentId={g.id} costs={g.costs} darfAnlegen={darfGeld} darfFreigeben={darfKostenFreigeben} />
              </div>
            ))}
          </div>
        )}
        {darfGeld && gruppen.length > 0 ? (
          neuerPostenShipment ? (
            <>
              {gruppen.length > 1 ? (
                <label className="mt-2.5 block text-[11px] text-ink-2">
                  Container
                  <select
                    value={neuerPostenShipment.id}
                    onChange={(e) => setNeuerPostenFuer(Number(e.target.value))}
                    className="mt-1 h-8 w-full rounded border border-line bg-background px-2 text-xs"
                  >
                    {gruppen.map((g) => <option key={g.id} value={g.id}>{containerTitelVon(g)}</option>)}
                  </select>
                </label>
              ) : null}
              <NeuerKostenposten key={neuerPostenShipment.id} shipmentId={neuerPostenShipment.id} err={err} kompakt
                onAbbrechen={() => setNeuerPostenFuer(null)} />
              <p className="mt-1.5 text-[11px] leading-snug text-ink-2">EUSt ist Vorsteuer — nur als Beleg ablegen, nicht als Kostenposten.</p>
            </>
          ) : (
            <Button type="button" variant="outline" size="sm" className="mt-2.5 w-full"
              onClick={() => setNeuerPostenFuer((gruppen.find((g) => g.costs.length === 0) ?? gruppen[0]!).id)}>
              Kosten erfassen
            </Button>
          )
        ) : null}
      </Panel>

      <Panel titel={shipment ? "Belege dieses Containers" : "Belege der Bestellung"}>
        <BelegeListe
          key={shipment?.id ?? "bestellung"}
          belege={belege}
          zielText={zielText}
          laeuft={uploadLaeuft}
          onDateien={darfBearbeiten ? onBelegeHochladen : undefined}
          aktionen={belegAktionen}
        />
      </Panel>

      {shipment ? <ContainerPanel key={shipment.id} shipment={shipment} darf={darfBearbeiten} /> : null}
    </div>
  );
}

/**
 * Eine Zahlung im Saldo: Zeile mit Betrag und ihren Anrechnungen auf Container; mit Preisrecht
 * korrigieren, anrechnen und (nach Rueckfrage) loeschen.
 */
function ZahlungsZeile({ zahlung, darf, shipments, titelVon, vorwahl }: {
  zahlung: PurchasePayment;
  darf: boolean;
  shipments: PurchaseShipment[];
  titelVon: (shipmentId: number) => string;
  /** Container der ausgewaehlten Spur, sonst der erste. */
  vorwahl: number | null;
}) {
  const loeschen = useDeletePurchasePayment();
  const [modus, setModus] = useState<"zeigen" | "bearbeiten" | "anrechnen" | "loeschen">("zeigen");
  const label = `${ZAHLUNG_TYP_TEXT[zahlung.typ] ?? zahlung.typ} ${datum(zahlung.paid_at)}`;

  return (
    <li>
      <div className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1 text-ink-2 [overflow-wrap:anywhere]">{label}</span>
        {darf && modus === "zeigen" ? (
          <>
            {shipments.length > 0 ? (
              <Button type="button" variant="ghost" size="sm" className="size-6 p-0"
                aria-label={`Zahlung ${zahlung.id} anrechnen`} title="Auf Container anrechnen" onClick={() => setModus("anrechnen")}>
                <Link2 className="size-3.5" />
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="sm" className="size-6 p-0"
              aria-label={`Zahlung ${zahlung.id} bearbeiten`} onClick={() => setModus("bearbeiten")}>
              <Pencil className="size-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="size-6 p-0"
              aria-label={`Zahlung ${zahlung.id} löschen`} onClick={() => setModus("loeschen")}>
              <Trash2 className="size-3.5 text-coral" />
            </Button>
          </>
        ) : null}
      </div>
      {/* Betraege in eigener Zeile: in der schmalen Spalte (272px) passt USD und EUR sonst nicht neben das Label. */}
      <div className="flex flex-wrap justify-end gap-x-3 font-mono">
        <span>{formatUsd(zahlung.amount_usd)}</span>
        <span>{zahlung.amount_eur === null ? "— €" : formatEuroGenau(zahlung.amount_eur)}</span>
      </div>
      {/* Neu aufbauen beim Oeffnen: die Vorwahl folgt der Spur, die gerade ausgewaehlt ist. */}
      <ZahlungAnrechnung key={modus === "anrechnen" ? `anrechnen-${vorwahl}` : "zeigen"}
        zahlung={zahlung} shipments={shipments} titelVon={titelVon} darf={darf} err={err} kompakt
        formular={modus === "anrechnen"} vorwahl={vorwahl} onFertig={() => setModus("zeigen")} />
      {darf && modus === "bearbeiten" ? (
        <ZahlungBearbeiten zahlung={zahlung} err={err} kompakt onFertig={() => setModus("zeigen")} />
      ) : null}
      {darf && modus === "loeschen" ? (
        <div className="mt-1.5 grid grid-cols-[minmax(0,1fr)] gap-2 rounded-md border border-coral/40 bg-coral-soft px-2.5 py-2 text-xs text-ink">
          <span>Zahlung löschen? Saldo und Kurs werden ohne sie neu gerechnet.</span>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="destructive" disabled={loeschen.isPending}
              onClick={() => loeschen.mutate(zahlung.id, {
                onSuccess: () => toast.success("Zahlung gelöscht"),
                onError: err("Löschen fehlgeschlagen"),
                onSettled: () => setModus("zeigen"),
              })}>
              Ja, löschen
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={loeschen.isPending} onClick={() => setModus("zeigen")}>
              Abbrechen
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

const alsBetragsfeld = (v: number | null) => (v === null ? "" : String(v).replace(".", ","));

/**
 * Ein Kostenposten. „erwartet“ steht offen zum Erfassen da (Betrag oder „entfällt“) — genau
 * das sperrt den Misch-EK. Andere Posten lassen sich mit „ändern“ neu erfassen. Gesendet wird
 * der volle Posten wie im Reiter Container (kostenPatchBody).
 */
function KostenZeile({ posten, darf, withPrices }: { posten: PurchaseCost; darf: boolean; withPrices: boolean }) {
  const patch = usePatchPurchaseCost();
  const offen = posten.status === "erwartet";
  const [aendern, setAendern] = useState(false);
  const [betrag, setBetrag] = useState(() => alsBetragsfeld(posten.amount_eur));
  const [meldung, setMeldung] = useState<string | null>(null);
  const name = posten.label ?? KOSTEN_TYP[posten.typ] ?? posten.typ;
  const editor = darf && (offen || aendern);

  function senden(status: "erfasst" | "geschaetzt" | "entfaellt") {
    let amount = posten.amount_eur;
    // „geschätzt“ braucht wie „erfasst“ einen Betrag (Server: parseCostBody).
    if (status !== "entfaellt") {
      const v = parseBetrag(betrag);
      if (v === undefined || (v !== null && v < 0)) return void setMeldung("Betrag nicht lesbar (z. B. 1.234,50)");
      if (v === null) return void setMeldung("Betrag eintragen oder „entfällt“ wählen");
      amount = v;
    }
    setMeldung(null);
    patch.mutate(
      { id: posten.id, body: kostenPatchBody(posten, status, amount) },
      {
        onSuccess: () => { setAendern(false); toast.success(`${name} ${KOSTEN_STATUS[status]}`); },
        onError: err("Kostenposten konnte nicht gespeichert werden"),
      },
    );
  }

  const wert = posten.status === "erfasst" || posten.status === "geschaetzt"
    ? (withPrices ? formatEuroGenau(posten.amount_eur) : KOSTEN_STATUS[posten.status])
    : KOSTEN_STATUS[posten.status] ?? posten.status;

  return (
    <li>
      <div className="flex items-center gap-1.5">
        <span className={`min-w-0 flex-1 truncate ${offen ? "text-warn" : "text-ink-2"}`}>{name}</span>
        <span className={`shrink-0 font-mono ${offen ? "text-warn" : ""}`}>{wert}</span>
        {posten.status === "geschaetzt" && withPrices ? <span className="text-[11px] text-ink-2">geschätzt</span> : null}
        {darf && !offen && !aendern ? (
          <Button type="button" variant="ghost" size="sm" className="size-6 p-0" aria-label={`${name} ändern`}
            onClick={() => { setBetrag(alsBetragsfeld(posten.amount_eur)); setMeldung(null); setAendern(true); }}>
            <Pencil className="size-3.5" />
          </Button>
        ) : null}
      </div>
      {editor ? (
        <div className="mt-1 grid grid-cols-[minmax(0,1fr)] gap-1.5">
          <div className="flex items-center gap-1.5">
            <Input
              inputMode="decimal"
              aria-label={`Betrag ${name} (EUR)`}
              placeholder="EUR"
              data-fokus-ziel={offen ? "kosten-erwartet" : undefined}
              value={betrag}
              onChange={(e) => setBetrag(e.target.value)}
              className="h-8 min-w-0 flex-1 text-xs"
            />
            <Button type="button" size="sm" className="h-8 px-2.5 text-xs" disabled={patch.isPending}
              aria-label={`${name} erfassen`} onClick={() => senden("erfasst")}>
              Erfassen
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" className="h-7 px-2.5 text-xs" disabled={patch.isPending}
              aria-label={`${name} geschätzt`} title="Betrag als Schätzung erfassen" onClick={() => senden("geschaetzt")}>
              Geschätzt
            </Button>
            <Button type="button" size="sm" variant="outline" className="h-7 px-2.5 text-xs" disabled={patch.isPending}
              aria-label={`${name} entfällt`} onClick={() => senden("entfaellt")}>
              Entfällt
            </Button>
            {aendern ? (
              <Button type="button" size="sm" variant="ghost" className="h-7 px-2.5 text-xs" disabled={patch.isPending}
                onClick={() => { setAendern(false); setMeldung(null); }}>
                Abbrechen
              </Button>
            ) : null}
          </div>
          {meldung ? <p role="alert" className="text-xs text-coral">{meldung}</p> : null}
        </div>
      ) : null}
    </li>
  );
}

const CONTAINER_FELDER = [
  ["container_no", "Container-Nr.", "text"], ["bl_no", "B/L-Nr.", "text"],
  ["booking_ref", "Booking", "text"], ["spediteur_ref", "Spediteur-Ref.", "text"], ["lieferanten_ref", "Lieferanten-Ref.", "text"],
  ["crd", "CRD", "date"], ["etd", "ETD", "date"], ["eta_pod", "ETA Hafen", "date"], ["eta_door", "ETA Tür", "date"],
] as const;
type ContainerFeld = (typeof CONTAINER_FELDER)[number][0];

/** Container-Daten ansehen und (mit Bearbeitungsrecht) aendern: derselbe PATCH wie im Reiter Container. */
function ContainerPanel({ shipment, darf }: { shipment: PurchaseShipment; darf: boolean }) {
  const patch = usePatchPurchaseShipment();
  const [entwurf, setEntwurf] = useState<Record<ContainerFeld, string> | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  // Stand beim Oeffnen: ein Neuladen waehrend der Bearbeitung darf fremde Aenderungen nicht ueberschreiben.
  const basis = useRef<Record<ContainerFeld, string> | null>(null);
  function oeffnen() {
    setFehler(null);
    const stand = Object.fromEntries(CONTAINER_FELDER.map(([f]) => [f, shipment[f] ?? ""])) as Record<ContainerFeld, string>;
    basis.current = stand;
    setEntwurf(stand);
  }
  function speichern() {
    if (!entwurf || !basis.current) return;
    const body: Partial<Record<ContainerFeld, string | null>> = {};
    for (const [feld] of CONTAINER_FELDER) {
      const v = entwurf[feld].trim();
      if (v !== basis.current[feld]) body[feld] = v === "" ? null : v;
    }
    if (Object.keys(body).length === 0) return void setEntwurf(null);
    setFehler(null);
    patch.mutate({ id: shipment.id, body }, {
      onSuccess: () => { setEntwurf(null); toast.success("Container-Daten gespeichert"); },
      onError: (e) => setFehler(fehlerText(e, "Speichern fehlgeschlagen")),
    });
  }

  return (
    <Panel titel="Container">
      {entwurf ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
          {CONTAINER_FELDER.map(([feld, label, typ]) => (
            <label key={feld} className="text-[11px] text-ink-2">
              {label}
              <Input type={typ} value={entwurf[feld]}
                onChange={(e) => setEntwurf((d) => (d ? { ...d, [feld]: e.target.value } : d))}
                className="mt-1 h-8 w-full text-xs" />
            </label>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" disabled={patch.isPending} onClick={speichern}>Daten speichern</Button>
            <Button type="button" size="sm" variant="ghost" disabled={patch.isPending} onClick={() => setEntwurf(null)}>Abbrechen</Button>
          </div>
          {fehler ? <p role="alert" className="text-xs text-coral">{fehler}</p> : null}
        </div>
      ) : (
        <>
          <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-[13px]">
            <Zeile label="Container-Nr.">{shipment.container_no ?? "—"}</Zeile>
            <Zeile label="B/L">{shipment.bl_no ?? "—"}</Zeile>
            <Zeile label="Booking">{shipment.booking_ref ?? "—"}</Zeile>
            <Zeile label="Spediteur-Ref.">{shipment.spediteur_ref ?? "—"}</Zeile>
            <Zeile label="Lieferanten-Ref.">{shipment.lieferanten_ref ?? "—"}</Zeile>
            <Zeile label="Status">{shipmentStatusLabel(shipment.status)}</Zeile>
            <Zeile label="CRD">{datum(shipment.crd)}</Zeile>
            <Zeile label="ETD">{datum(shipment.etd)}</Zeile>
            <Zeile label="ETA Hafen">{datum(shipment.eta_pod)}</Zeile>
            <Zeile label="ETA Tür">{datum(shipment.eta_door)}</Zeile>
            <Zeile label="Wareneingang">
              {shipment.warehouse_receipt_id !== null ? (
                <Link to={`/warehouse/wareneingang/${shipment.warehouse_receipt_id}`} className="text-primary hover:underline">
                  WE #{shipment.warehouse_receipt_id} · {weStatusLabel(shipment.we_status ?? "offen")}
                </Link>
              ) : "—"}
            </Zeile>
          </dl>
          {darf ? (
            <Button type="button" variant="outline" size="sm" className="mt-2.5 w-full" onClick={oeffnen}>
              Container-Daten bearbeiten
            </Button>
          ) : null}
        </>
      )}
    </Panel>
  );
}

function Zeile({ label, warn, children }: { label: string; warn?: boolean; children: React.ReactNode }) {
  const tone = warn ? "text-warn" : "";
  return (
    <>
      <dt className={`min-w-0 truncate ${tone || "text-ink-2"}`}>{label}</dt>
      <dd className={`min-w-0 text-right font-mono [overflow-wrap:anywhere] ${tone}`}>{children}</dd>
    </>
  );
}
